<script lang="ts">
const DEFAULT_SIZES = {
  topPanelHeight: 240,
  bottomPanelHeight: 470, // 40%
};

const MIN_ROW_HEIGHT = 0;

const drivers = ['sessionStorage'];
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref, shallowRef } from 'vue';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import MarketplaceOrdersList from '@/modules/marketplace/components/MarketplaceOrdersList.vue';
import MarketplaceActionConfirmationSnackbar from '@/modules/marketplace/components/MarketplaceActionConfirmationSnackbar.vue';
import MarketplaceOrderDetailsCard from '@/modules/marketplace/components/MarketplaceOrderDetailsCard.vue';
import MarketplaceOrderForm from '@/modules/marketplace/components/MarketplaceOrderForm.vue';
import { ConfirmationSnackbarOptions, OrderSide } from '@/modules/marketplace/types/marketplace';
import LoanDetailsDialog from '@/modules/open-loans/components/LoanDetailsDialog.vue';
import SnapshotManager from '@/modules/common/components/SnapshotManager.vue';
import { OmsOrder } from '@/modules/marketplace/models';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { getStatus } from '@/modules/marketplace/helpers/marketplace';
import MarketplaceToggleRouteBtn from '@/modules/marketplace/components/MarketplaceToggleRouteBtn.vue';
import MarketplaceCancelBtn from '@/modules/marketplace/components/MarketplaceCancelBtn.vue';
import { store } from '@/store/store';

provide('gridStateKey', 'MarketplaceOrders');

const hasTraderUserRole = computed(() => store.getters.hasTraderUserRole);

const detailOrderRef = shallowRef<string | null>(null);
const confirmationSnackbarOptions = shallowRef<ConfirmationSnackbarOptions | null>(null);
const editOrder = shallowRef<FrontendOrder | OmsOrder | null>(null);
const loadedOrder = shallowRef<OmsOrder | null>(null);
const newOrderSide = shallowRef<OrderSide | null>(null);
const detailLoanId = shallowRef<string | null>(null);

const startY = shallowRef(0);
const sizes = ref({ ...DEFAULT_SIZES });
const storedSizes = ref({ ...DEFAULT_SIZES });
const currentScreenY = shallowRef(window.screenY);

const topPanelRef = shallowRef<HTMLElement | null>(null);
const bottomPanelRef = shallowRef<HTMLElement | null>(null);

const snapshot = computed(() => ({
  sizes: JSON.stringify(sizes.value),
  payload: JSON.stringify({
    detailOrderRef: detailOrderRef.value,
  }),
}));

function handleScreenChange(): void {
  if (window.screenY !== currentScreenY.value) {
    // because our users might have multiple monitors
    // we need to reset the panels widths and heights to inital values
    sizes.value = { ...DEFAULT_SIZES };
    storedSizes.value = { ...DEFAULT_SIZES };
  }
}

function startResizeH(event: MouseEvent): void {
  document.body.style.cursor = 'row-resize';
  startY.value = event.clientY;

  const onMouseMove = (e: MouseEvent) => {
    const deltaY = e.clientY - startY.value;

    // re-assign real width
    sizes.value.topPanelHeight = topPanelRef.value?.clientHeight ?? sizes.value.topPanelHeight;
    sizes.value.bottomPanelHeight =
      bottomPanelRef.value?.clientHeight ?? sizes.value.bottomPanelHeight;

    const shouldStopResizing =
      sizes.value.bottomPanelHeight - deltaY >= MIN_ROW_HEIGHT &&
      sizes.value.topPanelHeight + deltaY >= MIN_ROW_HEIGHT;

    if (shouldStopResizing) {
      sizes.value.topPanelHeight += deltaY;
      sizes.value.bottomPanelHeight -= deltaY;
      startY.value = e.clientY;
    }
  };

  const onMouseUp = () => {
    document.body.style.cursor = 'auto';
    storedSizes.value = { ...sizes.value };
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
}

function openCreateDialog(side: 'BORROW' | 'LOAN'): void {
  newOrderSide.value = side;
  editOrder.value = null;
}

function openEditDialog(order: FrontendOrder | OmsOrder): void {
  newOrderSide.value = null;
  editOrder.value = order;
}

function getStateMutators(): ReturnType<SnapshotManager['getStateMutators']> {
  return {
    sizes: (value) => {
      sizes.value = JSON.parse(value);
    },
    payload: (value) => {
      const payload = JSON.parse(value);
      if (payload.detailOrderRef !== undefined) {
        detailOrderRef.value = payload.detailOrderRef;
      }
    },
  };
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleScreenChange);
});

onMounted(() => {
  storedSizes.value = { ...sizes.value };
  window.addEventListener('resize', handleScreenChange);
});

defineExpose({
  test: {
    confirmationSnackbarOptions,
    detailOrderRef,
  },
});
</script>

<template>
  <SnapshotManager
    :drivers="drivers"
    :get-state-mutators="getStateMutators"
    :snapshot="snapshot"
    storage-key="snapshot-orders-explorer"
  >
    <div class="d-flex flex-column fill-height full-width flex-grow-1">
      <div
        ref="topPanelRef"
        :class="['d-flex flex-column fill-width pa-0 col']"
        :style="{ height: sizes.topPanelHeight + 'px' }"
      >
        <DashboardPanel no-collapse title="orders">
          <MarketplaceOrdersList
            @action="
              confirmationSnackbarOptions = {
                ...$event,
                showViewButton: detailOrderRef !== $event.orderRef,
              }
            "
            @create-order="openCreateDialog($event)"
            @edit-order="openEditDialog($event)"
            @view-order="detailOrderRef = $event"
          />
          <MarketplaceActionConfirmationSnackbar
            v-if="confirmationSnackbarOptions"
            :options="confirmationSnackbarOptions"
            @close="confirmationSnackbarOptions = null"
            @view-order="detailOrderRef = confirmationSnackbarOptions.orderRef"
          />
        </DashboardPanel>
      </div>
      <div :class="['resize-handle-h', { 'd-none': !detailOrderRef }]" @mousedown="startResizeH" />
      <div
        v-if="detailOrderRef"
        ref="bottomPanelRef"
        class="d-flex flex-column fill-width pa-0 flex-1"
        :style="{ height: sizes.bottomPanelHeight + 'px' }"
      >
        <DashboardPanel no-collapse show-close-icon @close="detailOrderRef = null">
          <template #title>
            <div>
              Order <span style="user-select: all">{{ detailOrderRef }}</span>
              <v-chip
                v-if="loadedOrder"
                class="justify-center text-lowercase ml-2"
                :color="getStatus(loadedOrder).color"
                x-small
              >
                {{ getStatus(loadedOrder).label }}
              </v-chip>
            </div>
            <div
              v-if="loadedOrder && ['OPEN', 'HELD'].includes(loadedOrder.status)"
              class="order-controls"
            >
              <MarketplaceToggleRouteBtn
                :order-ref="loadedOrder.orderRef"
                :status="loadedOrder.status"
                @action="confirmationSnackbarOptions = $event"
              />

              <MarketplaceCancelBtn
                :order-ref="loadedOrder.orderRef"
                @action="confirmationSnackbarOptions = $event"
              />

              <aurora-btn
                color="secondary"
                data-test="edit-order-btn"
                :disabled="!hasTraderUserRole"
                timeframe="createLoans"
                @click="openEditDialog(loadedOrder)"
              >
                Edit
              </aurora-btn>
            </div>
          </template>
          <MarketplaceOrderDetailsCard
            hide-buttons
            :order-ref="detailOrderRef"
            should-retry-fetch
            @close-modal="detailOrderRef = null"
            @open-loan-details="detailLoanId = $event"
            @order-loaded="loadedOrder = $event"
          />
        </DashboardPanel>
      </div>
      <LoanDetailsDialog
        v-if="detailLoanId"
        :loan-id="detailLoanId"
        should-retry-fetch
        @close-modal="detailLoanId = null"
      />
      <MarketplaceOrderForm
        :new-order-side="newOrderSide"
        :value="!!newOrderSide"
        @action="confirmationSnackbarOptions = { ...$event, showViewButton: true }"
        @input="!$event && (newOrderSide = null)"
      />
      <MarketplaceOrderForm
        :order-ref="editOrder?.orderRef"
        :value="!!editOrder"
        @action="
          confirmationSnackbarOptions = {
            ...$event,
            showViewButton: detailOrderRef !== $event.orderRef,
          }
        "
        @input="!$event && (editOrder = null)"
      />
    </div>
  </SnapshotManager>
</template>

<style lang="scss">
.resize-handle-h {
  user-select: none;
  height: 5px;
  border: 2px solid transparent;
  cursor: row-resize;
  user-select: none;
  width: calc(100% - 16px);
  margin-left: 8px;
  &:hover {
    background: #ebeced;
    opacity: 0.1;
  }
}

.order-controls {
  display: flex;
  gap: 8px;
  margin-left: auto;
  margin-right: 16px;
  align-items: center;
  line-height: 0;

  .v-btn {
    height: 20px !important;
  }
}
</style>
